import { Chip, ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { TsrctCaseCancellationReason, TsrctSourcingCaseStatus } from '@/generated/gql/graphql';

export type SourcingCaseStatusUnion = `${TsrctSourcingCaseStatus}`;

const propsByStatusMap: {
  [key in SourcingCaseStatusUnion]: { color: ChipProps['color'] };
} = {
  [TsrctSourcingCaseStatus.RELEASABLE]: { color: 'default' },
  [TsrctSourcingCaseStatus.RELEASED]: { color: 'info' },
  [TsrctSourcingCaseStatus.OFFER_DISMISSED]: { color: 'default' },
  [TsrctSourcingCaseStatus.DRAFT]: { color: 'default' },
  [TsrctSourcingCaseStatus.CANCELLED]: { color: 'error' },
  [TsrctSourcingCaseStatus.SUPPLIER_AGREEMENT_REACHED]: { color: 'default' },
  [TsrctSourcingCaseStatus.AWARDED]: { color: 'success' },
};

export interface SourcingCaseStatusChipProps {
  className?: string;
  status: SourcingCaseStatusUnion;
  cancellationReason?: Union.Nullable<TsrctCaseCancellationReason | `${TsrctCaseCancellationReason}`>;
  cancellationReasonText?: Union.Nullable<string>;
}

export const SourcingCaseStatusChip = ({
  className,
  status,
  cancellationReason,
  cancellationReasonText,
}: SourcingCaseStatusChipProps) => {
  const { t } = useTranslation();

  if (status) {
    const { color } = propsByStatusMap[status];
    const baseTitle = t(`SourcingCaseStatus.${status}`);
    const reasonLabel = cancellationReason ? t(`CaseCancellationReason.${cancellationReason}`) : null;
    const title =
      status === TsrctSourcingCaseStatus.CANCELLED && reasonLabel
        ? `${reasonLabel}${cancellationReasonText ? ` — ${cancellationReasonText}` : ''}`
        : baseTitle;

    return (
      <Chip
        className={className}
        label={t(`SourcingCaseStatus.${status}`)}
        color={color}
        data-intercom-target="SourcingCase/StatusChip"
        data-testid="SourcingCase/StatusChip"
        title={title}
      />
    );
  }

  return null;
};
